import React, { memo, useMemo, useEffect, useState, useCallback } from 'react';
import { getAllConditionEntries, getDependencyTaskId, getDependencyBranch } from '../../shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * Get upstream tasks for a task - from unlockConditions (v1 or v2) or fallback to dependsOn
 */
function getUpstreamTasks(task, allTasks) {
  const entries = getAllConditionEntries(task.unlockConditions, allTasks);
  if (entries.length > 0) {
    const seen = new Set();
    return entries.filter(e => {
      if (seen.has(e.taskId)) return false;
      seen.add(e.taskId);
      return true;
    });
  }
  const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
  return deps.map(dep => ({
    taskId: getDependencyTaskId(dep),
    branch: getDependencyBranch(dep),
  })).filter(d => d.taskId);
}

/**
 * Generate dependency edges from tasks
 */
function generateDependencyEdges(tasks) {
  const taskMap = new Map(tasks.map(t => [t.id, t]));
  const edges = [];
  
  tasks.forEach(task => {
    const upstreamTasks = getUpstreamTasks(task, tasks);
    
    upstreamTasks.forEach(({ taskId: fromId, branch }) => {
      if (!fromId) return;
      
      const fromTask = taskMap.get(fromId);
      if (!fromTask) return;
      
      // Get branch color from decision node's options
      let branchColor = null;
      if (fromTask?.nodeType === 'decision' && branch) {
        const options = fromTask.options || [
          { id: 'yes', label: 'Yes', color: '#10b981' },
          { id: 'no', label: 'No', color: '#ef4444' },
        ];
        const option = options.find(opt => opt.id === branch);
        if (option) {
          branchColor = option.color;
        }
      }
      
      edges.push({
        id: `${fromId}-${task.id}${branch ? `-${branch}` : ''}`,
        from: fromId,
        to: task.id,
        fromTask,
        toTask: task,
        branchColor,
        isFromDecision: fromTask?.nodeType === 'decision',
      });
    });
  });
  
  return edges;
}

/**
 * DependencyLines Component
 * 
 * Renders SVG lines between dependent tasks in the Gantt chart.
 * Lines connect from the end of the source task bar to the start of the dependent task bar.
 */
const DependencyLines = memo(({
  tasks,
  chartRef,
}) => {
  // Track bar positions from DOM
  const [barPositions, setBarPositions] = useState(new Map());
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  
  // Generate edge data from tasks
  const edges = useMemo(() => {
    return generateDependencyEdges(tasks);
  }, [tasks]);
  
  // Update bar positions when tasks change or container scrolls/resizes
  const updatePositions = useCallback(() => {
    const chart = chartRef?.current;
    if (!chart || edges.length === 0) return;
    
    const chartRect = chart.getBoundingClientRect();
    
    // SVG should cover the full scrollable area of the chart
    setDimensions({
      width: chart.scrollWidth,
      height: chart.scrollHeight,
    });
    
    const positions = new Map();
    
    // Find all gantt bars and get their positions relative to the chart
    const bars = chart.querySelectorAll('.gantt-bar');
    bars.forEach(bar => {
      const taskId = bar.dataset.taskId;
      if (!taskId) return;
      
      const rect = bar.getBoundingClientRect();
      
      // Calculate position relative to the chart container (accounting for scroll)
      const left = rect.left - chartRect.left + chart.scrollLeft;
      const top = rect.top - chartRect.top + chart.scrollTop;
      
      positions.set(taskId, {
        left: left,
        right: left + rect.width,
        top: top,
        bottom: top + rect.height,
        centerY: top + rect.height / 2,
        width: rect.width,
        height: rect.height,
      });
    });
    
    setBarPositions(positions);
  }, [chartRef, edges.length]);
  
  useEffect(() => {
    if (!chartRef?.current || edges.length === 0) return;
    
    const chart = chartRef.current;
    
    // Debounce to prevent rapid updates
    let timeoutId = null;
    const debouncedUpdate = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(updatePositions, 16); // ~60fps max
    };
    
    // Initial update
    updatePositions();
    
    // Update on scroll
    chart.addEventListener('scroll', debouncedUpdate);
    
    // Update on resize
    const resizeObserver = new ResizeObserver(debouncedUpdate);
    resizeObserver.observe(chart);
    
    // Update on mouseup (after drag ends)
    const handleMouseUp = () => {
      setTimeout(updatePositions, 50);
    };
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      chart.removeEventListener('scroll', debouncedUpdate);
      resizeObserver.disconnect();
      document.removeEventListener('mouseup', handleMouseUp);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [chartRef, edges.length, updatePositions]);
  
  // Calculate SVG paths for each edge - orthogonal (right-angle) routing
  const paths = useMemo(() => {
    if (barPositions.size === 0) return [];
    
    return edges.map(edge => {
      const fromPos = barPositions.get(edge.from);
      const toPos = barPositions.get(edge.to);
      
      if (!fromPos || !toPos) return null;
      
      // Arrow goes from right side of source to left side of target
      const startX = fromPos.right;
      const startY = fromPos.centerY;
      const endX = toPos.left;
      const endY = toPos.centerY;
      
      const dx = endX - startX;
      const dy = endY - startY;
      
      let path;
      
      // Minimum horizontal gap for the vertical segment
      const minGap = 10;
      
      if (dx >= minGap) {
        // FORWARD PATH: Target left edge is to the right of source right edge
        // Simple L-shape or straight line
        
        if (Math.abs(dy) < 2) {
          // Same row - straight horizontal line
          path = `M ${startX} ${startY} L ${endX} ${endY}`;
        } else {
          // Different rows - go right halfway, drop/rise, go right to target
          const midX = startX + dx / 2;
          path = `M ${startX} ${startY} ` +
                 `L ${midX} ${startY} ` +
                 `L ${midX} ${endY} ` +
                 `L ${endX} ${endY}`;
        }
      } else {
        // BACKWARD/OVERLAPPING PATH: Target starts before source ends
        // Route: exit right, drop to just below source, go left, drop to target row, go right into target
        
        const routeGap = 15;
        const exitX = startX + routeGap;
        const entryX = endX - routeGap;
        
        // First horizontal routing lane - just below/above the SOURCE bar
        // This is where we travel horizontally (going left)
        const fromBottom = fromPos.bottom;
        const fromTop = fromPos.top;
        
        // Second stop - align with entry point horizontally, then drop to target
        let routeY;
        if (endY > startY) {
          // Target is below - route just below the source bar, then drop further
          routeY = fromBottom + 12;
        } else if (endY < startY) {
          // Target is above - route just above the source bar, then rise further
          routeY = fromTop - 12;
        } else {
          // Same row but overlapping - go below source
          routeY = fromBottom + 12;
        }
        
        path = `M ${startX} ${startY} ` +
               `L ${exitX} ${startY} ` +        // Go right from source
               `L ${exitX} ${routeY} ` +        // Drop/rise to just below/above source
               `L ${entryX} ${routeY} ` +       // Go left to entry X position
               `L ${entryX} ${endY} ` +         // Drop/rise to target row
               `L ${endX} ${endY}`;             // Go right into target
      }
      
      return {
        id: edge.id,
        path,
        color: edge.branchColor || 'rgba(255, 255, 255, 0.4)',
        isDecision: edge.isFromDecision,
        startX, startY, endX, endY,
      };
    }).filter(Boolean);
  }, [edges, barPositions]);
  
  if (paths.length === 0 || dimensions.width === 0) return null;
  
  return (
    <svg
      className="gantt-dependency-lines"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: dimensions.width,
        height: dimensions.height,
        pointerEvents: 'none',
        overflow: 'visible',
        zIndex: 0,
      }}
    >
      <defs>
        {/* Arrow marker for line ends - default color */}
        <marker
          id="gantt-arrow-default"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="5"
          markerHeight="5"
          orient="auto-start-reverse"
        >
          <path
            d="M 0 0 L 10 5 L 0 10 z"
            fill="rgba(255, 255, 255, 0.5)"
          />
        </marker>
        {/* Colored arrow markers for decision branches */}
        {paths.filter(p => p.isDecision).map(p => (
          <marker
            key={`arrow-${p.id}`}
            id={`gantt-arrow-${p.id}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="5"
            markerHeight="5"
            orient="auto-start-reverse"
          >
            <path
              d="M 0 0 L 10 5 L 0 10 z"
              fill={p.color}
            />
          </marker>
        ))}
      </defs>
      
      {paths.map(p => (
        <path
          key={p.id}
          d={p.path}
          fill="none"
          stroke={p.color}
          strokeWidth={p.isDecision ? 2 : 1.5}
          strokeOpacity={p.isDecision ? 0.8 : 0.6}
          strokeLinecap="round"
          markerEnd={p.isDecision ? `url(#gantt-arrow-${p.id})` : 'url(#gantt-arrow-default)'}
          className="gantt-dependency-line"
        />
      ))}
    </svg>
  );
});

DependencyLines.displayName = 'DependencyLines';

export default DependencyLines;
